import { useTheme } from '@fluentui/react';
import { IPFontIcon, IPIconButton, IPLink, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { acceptIcon, globalStackTokensGapExtraSmall, globalStackTokensGapSmall } from 'globalStyles';
import { useTranslation } from 'react-i18next';

export interface IEvidenceTaskLinkItem {
  taskId: number;
  taskName?: string;
  // The task is completed or archived, which verifies a one-time criterion. Absent on surfaces that
  // build their items from binding rows, which carry no task state.
  taskClosed?: boolean;
}

interface IEvidenceTaskLinksProps {
  tasks: IEvidenceTaskLinkItem[];
  onOpenTask: (taskId: number) => void;
  // Without a handler the unlink icon is not rendered: read-only surfaces (audit checklist) only
  // offer the link to the task.
  onUnlink?: (taskId: number) => void;
  disabled?: boolean;
  hideEmpty?: boolean;
}

// The evidence tasks bound to one check: per task a wrapping link that opens the task, its
// verification state and an unlink icon behind it. Shared between the criterion panel and the audit
// execution forms.
const EvidenceTaskLinks = (props: IEvidenceTaskLinksProps) => {
  const { t } = useTranslation(['criteria']);
  const theme = useTheme();

  if (props.tasks.length === 0) {
    if (props.hideEmpty) return null;

    return <IPText>{'-'}</IPText>;
  }

  //the name must wrap INSIDE its own cell: without an explicit wrap and a non-shrinking icon item,
  //a long name renders across the unlink icon and clicks meant for the link hit the icon instead.
  //data-selection-disabled + stopPropagation keep a click here out of the list's row selection.
  return (
    <IPStack
      tokens={globalStackTokensGapSmall}
      data-selection-disabled={true}
      onClick={(ev) => ev.stopPropagation()}
    >
      {props.tasks.map((task) => (
        <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall} key={task.taskId}>
          <IPStack.Item grow shrink styles={{ root: { minWidth: 0, overflow: 'hidden' } }}>
            <IPText block styles={{ root: { wordBreak: 'break-word' } }}>
              <IPLink
                disabled={props.disabled}
                styles={{ root: { whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'left' } }}
                onClick={(ev) => {
                  ev.stopPropagation();
                  props.onOpenTask(task.taskId);
                }}
              >
                {/* a bound task whose name did not resolve (hidden or a series row) stays openable */}
                {task.taskName ? task.taskName : `#${task.taskId}`}
              </IPLink>
            </IPText>
          </IPStack.Item>
          {task.taskClosed && (
            <IPStack.Item disableShrink>
              <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapExtraSmall}>
                <IPFontIcon {...acceptIcon} style={{ color: theme.semanticColors.successText }} />
                <IPText
                  variant="small"
                  styles={{ root: { color: theme.semanticColors.successText, whiteSpace: 'nowrap' } }}
                >
                  {t('criteria:Tasks.State.Completed')}
                </IPText>
              </IPStack>
            </IPStack.Item>
          )}
          {props.onUnlink && (
            <IPStack.Item disableShrink>
              <IPIconButton
                iconProps={{ iconName: 'Delete' }}
                title={t('criteria:Tasks.Unlink')}
                disabled={props.disabled}
                onClick={(ev) => {
                  ev.stopPropagation();
                  props.onUnlink?.(task.taskId);
                }}
              />
            </IPStack.Item>
          )}
        </IPStack>
      ))}
    </IPStack>
  );
};

export default EvidenceTaskLinks;
